import React, { ReactNode, useContext, useEffect } from 'react';

import { Anchor, ButtonAnchor, Carousel, CarouselSlide, Rating } from '@zooplus/ui-components';

import LocResourcesContext from '../../../common/contexts/LocResourcesContext';
import { PDPProduct } from '../../model/BonusPointsProduct';
import { ViewType } from '../../model/ViewType';
import PDPAddToCart from '../add-to-cart/PDPAddToCart';

import CustomerPictures from './CustomerPictures';

interface PDPUpperInfoProps {
  product: PDPProduct;
  mediaServerUrl: string;
  reviewNumberTranslation: string;
  redeemableBalance: number;
  selection: number[];
  view: ViewType;
  onRenderSuccess: () => void;
}

const PDPUpperInfo: React.FC<PDPUpperInfoProps> = ({
  product,
  mediaServerUrl,
  reviewNumberTranslation,
  redeemableBalance,
  selection,
  view,
  onRenderSuccess,
}: PDPUpperInfoProps) => {
  const GLOBAL_PREFIX = 'loy-bp-pdppui';
  const { onFormatLocale } = useContext(LocResourcesContext);

  function getProductImage(imageName: string, size: number): string {
    const folderPattern = /([a-zA-Z0-9])?\.(.*)$/;
    const groups = folderPattern.exec(imageName);
    let folderShortcut = '';
    if (groups && groups.length >= 1) {
      folderShortcut = groups[1];
    }
    return `https://${mediaServerUrl}/bilder/${folderShortcut}/${size}/${imageName}`;
  }

  function getCarouselSlides(): ReactNode[] {
    return product.pictures.map((picture) => {
      return (
        <CarouselSlide
          key={picture}
          className={`${GLOBAL_PREFIX}_carousel-slide`}
          thumb={getProductImage(picture, 50)}>
          <img src={getProductImage(picture, 800)} alt={product.title} />
        </CarouselSlide>
      );
    });
  }

  useEffect(() => {
    if (onFormatLocale) onRenderSuccess();
  }, [onFormatLocale]);

  return (
    <div className={`${GLOBAL_PREFIX}_wrapper`}>
      <Carousel
        id={'pdp-carousel'}
        lightbox={{
          ariaLabel: onFormatLocale!('bp.productdetail.carrousel.lightbox.name'),
          closeButtonAriaLabel: onFormatLocale!('bp.productdetail.carrousel.lightbox.closebutton'),
          openButtonAriaLabel: onFormatLocale!('bp.productdetail.carrousel.lightbox.openbutton'),
          title: product.title,
        }}
        nextButtonAriaLabel={onFormatLocale!('bp.productdetail.carrousel.nextbutton')}
        previousButtonAriaLabel={onFormatLocale!('bp.productdetail.carrousel.previousbutton')}
        thumbItemPrefixLabel={onFormatLocale!('bp.productdetail.carrousel.thumbItemPrefixLabel')}>
        {getCarouselSlides()}
      </Carousel>
      <div>
        <div>
          <p
            dangerouslySetInnerHTML={{ __html: product.summary }}
            className={`${GLOBAL_PREFIX}_summary`}></p>
          <Anchor href={'#pdp-product-details'} className={`${GLOBAL_PREFIX}_more-info-link`}>
            {onFormatLocale!('general.more.information')}
          </Anchor>

          <div className={`${GLOBAL_PREFIX}_rating-teaser`}>
            <Rating
              rating={product.averageRating}
              ratingLabel={onFormatLocale!('bp.productdetail.rating.label')}
              notRatedLabel={onFormatLocale!('bp.productdetail.rating.notrated.label')}
            />
            <span>{reviewNumberTranslation}</span>
          </div>

          <CustomerPictures product={product} />

          <div className={`${GLOBAL_PREFIX}_add-to-cart-box`}>
            <div className={'cost'}>
              {product.reducedBonuspoints <= 0 ? (
                <span>{`${product.bonuspoints} ${onFormatLocale!('bp.points')}`}</span>
              ) : (
                <>
                  <span className={'new-points'}>
                    <span>{`${product.reducedBonuspoints} ${onFormatLocale!('bp.points')}`}</span>
                  </span>
                  <del className={'old-points'} aria-describedby="reference-zooPoints-desc">
                    {product.bonuspoints}
                  </del>
                  <span id="reference-zooPoints-desc" className="pp-visually-hidden">
                    {onFormatLocale!('bp.productdetail.addtocart.referencezoopoints.desc', {
                      number: product.bonuspoints,
                    })}
                  </span>
                </>
              )}
            </div>
            {view === 'private' ? (
              <PDPAddToCart
                product={product}
                redeemableBalance={redeemableBalance}
                selection={selection}
              />
            ) : (
              <>
                <ButtonAnchor
                  length={'normal'}
                  className={'login-button'}
                  href={`/account${document.location.pathname}`}>
                  {onFormatLocale?.('general.button.signin')}
                </ButtonAnchor>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(PDPUpperInfo);
